import clsx from 'clsx';
import type { Bucket, MediaItem } from '../../types';
import { useThumbnail } from '../hooks/useImageUrl';

/**
 * A responsive wall of frames from a shoot.
 *
 * Replaces the horizontal strip, which clipped its last thumbnail against the
 * card edge and needed a scrollbar to see the rest. A grid shows whole frames at
 * every width, and the overflow is stated as a number instead of a cut-off tile.
 */
export default function ShootPreview({
  items,
  buckets,
  total,
  picks,
  onOpen,
}: {
  items: MediaItem[];
  buckets: Bucket[];
  total: number;
  /** Media ids Review Assist suggested, so the badge matches the culling screen. */
  picks?: ReadonlySet<number>;
  onOpen?: () => void;
}) {
  if (items.length === 0) {
    return (
      <div className="checker aspect-[3/1] w-full rounded-xl border border-ink-800" aria-hidden="true" />
    );
  }

  const shown = items.slice(0, 11);
  const remaining = Math.max(0, total - shown.length);

  return (
    <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6 lg:grid-cols-12">
      {shown.map((item) => (
        <Frame
          key={item.id}
          item={item}
          buckets={buckets}
          isPick={picks?.has(item.id) ?? false}
          onOpen={onOpen}
        />
      ))}

      {remaining > 0 && (
        <button
          onClick={onOpen}
          className="tnum flex aspect-square items-center justify-center rounded-md border border-ink-800 bg-ink-900 text-caption text-ink-400 transition hover:border-ink-600 hover:text-ink-100"
        >
          +{remaining}
        </button>
      )}
    </div>
  );
}

function Frame({
  item,
  buckets,
  isPick,
  onOpen,
}: {
  item: MediaItem;
  buckets: Bucket[];
  isPick: boolean;
  onOpen?: () => void;
}) {
  const url = useThumbnail(item);
  const bucket = buckets.find((b) => b.id === item.bucketId);

  return (
    <button
      onClick={onOpen}
      aria-label={item.filename}
      className="group relative aspect-square overflow-hidden rounded-md bg-ink-850"
    >
      {url ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06] motion-reduce:transition-none"
          style={item.rotation ? { transform: `rotate(${item.rotation}deg)` } : undefined}
        />
      ) : (
        <div className="h-full w-full animate-pulse2 bg-ink-800" />
      )}

      {/* The same teal badge as the filmstrip. One mark, one meaning, wherever
          a frame is shown. */}
      {isPick && (
        <span
          title="Suggested by Review Assist: the sharpest of this burst"
          className="absolute right-1.5 top-1.5 flex h-5 items-center rounded-full bg-pick px-2 text-[10px] font-bold tracking-tight text-ink-950 ring-2 ring-ink-950/50"
        >
          A.I.
        </span>
      )}

      {bucket && (
        <span
          className={clsx(
            'absolute inset-x-0 bottom-0 h-[3px]',
            bucket.kind === 'keep' ? 'bg-keep' : 'bg-reject',
          )}
        />
      )}
    </button>
  );
}
